import React, { useMemo } from "react";
import { Card, Row, Col, Statistic, Spin, Alert } from "antd";
import {
  DollarSign,
  TrendingUp,
  Users,
  Package,
  Calculator,
  Truck,
  CheckCircle,
} from "lucide-react";
import {
  useGetServiceMetrics,
  useGetPackingMetrics,
} from "~/services/service-metrics";
import { useGetStaffRoles } from "~/services/common";
import { useGetServiceTickets } from "~/services/service-ticket/service-ticket.queries";
import { JobStatus, SortOrder } from "~/utils/constants";

export const ServiceMetricsOverview: React.FC = () => {
  const { data: serviceMetrics, isLoading: metricsLoading } =
    useGetServiceMetrics();
  const { data: staffRoles, isLoading: staffLoading } = useGetStaffRoles();
  const { data: packingMetrics, isLoading: packingLoading } =
    useGetPackingMetrics();
  const { data: serviceTicketsData, isLoading: ticketsLoading } = useGetServiceTickets(
    {
      page: 1,
      pageSize: 1000, // Get a large dataset for accurate counts
      sortByCreatedAt: SortOrder.DESC,
    },
    true
  );

  const isLoading = metricsLoading || staffLoading || packingLoading || ticketsLoading;

  // Calculate service ticket statistics - MUST be before any early returns
  const serviceTicketStats = useMemo(() => {
    const tickets = serviceTicketsData?.items || [];
    const totalTickets = tickets.length;
    const completedTickets = tickets.filter(
      (t) => t.status === JobStatus.COMPLETED
    ).length;
    const activeTickets = tickets.filter(
      (t) =>
        t.status === JobStatus.SCHEDULED ||
        t.status === JobStatus.IN_PROGRESS
    ).length;

    return {
      total: totalTickets,
      completed: completedTickets,
      active: activeTickets,
    };
  }, [serviceTicketsData]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  const totalServiceMetrics = serviceMetrics?.length || 0;
  const activeServiceMetrics =
    serviceMetrics?.filter((m) => m.isActive)?.length || 0;
  const totalStaffRoles = staffRoles?.items?.length || 0;
  const activeStaffRoles = staffRoles?.items?.length || 0; // All staff roles are considered active
  const totalPackingMetrics = packingMetrics?.length || 0;
  const activePackingMetrics =
    packingMetrics?.filter((p) => p.isActive)?.length || 0;

  // Calculate average rates
  const avgStaffRate =
    (staffRoles?.items || []).reduce((sum, role) => sum + role.hourlyRate, 0) /
      (staffRoles?.items?.length || 1) || 0;
  const avgPackingRate =
    (packingMetrics || []).reduce(
      (sum, metric) => sum + (Number(metric.baseRate) || 0),
      0
    ) / (packingMetrics?.length || 1) || 0;

  const overviewCards = [
    {
      title: "Total Service Metrics",
      value: totalServiceMetrics,
      subtitle: `${activeServiceMetrics} active`,
      icon: <Calculator className="text-gray-600" size={16} />,
    },
    {
      title: "Staff Roles",
      value: totalStaffRoles,
      subtitle: `${activeStaffRoles} active`,
      icon: <Users className="text-gray-600" size={16} />,
    },
    {
      title: "Packing Metrics",
      value: totalPackingMetrics,
      subtitle: `${activePackingMetrics} active`,
      icon: <Package className="text-gray-600" size={16} />,
    },
    {
      title: "Avg Staff Rate",
      value: `$${avgStaffRate.toFixed(2)}`,
      subtitle: "per hour",
      icon: <DollarSign className="text-gray-600" size={16} />,
    },
    {
      title: "Avg Packing Rate",
      value: `$${avgPackingRate.toFixed(2)}`,
      subtitle: "per cubic foot",
      icon: <TrendingUp className="text-gray-600" size={16} />,
    },
    {
      title: "Service Types",
      value: new Set(serviceMetrics?.map((m) => m.serviceType))?.size || 0,
      subtitle: "different types",
      icon: <Truck className="text-gray-600" size={16} />,
    },
    {
      title: "Total Service Tickets",
      value: serviceTicketStats.total,
      subtitle: `${serviceTicketStats.active} active`,
      icon: <Calculator className="text-gray-600" size={16} />,
    },
    {
      title: "Completed Tickets",
      value: serviceTicketStats.completed,
      subtitle: `${serviceTicketStats.total > 0 
        ? ((serviceTicketStats.completed / serviceTicketStats.total) * 100).toFixed(1) 
        : 0}% completion rate`,
      icon: <CheckCircle className="text-gray-600" size={16} />,
    },
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Overview Cards */}
      <Row gutter={[12, 12]}>
        {overviewCards.map((card, index) => (
          <Col xs={12} sm={8} md={6} lg={3} key={index}>
            <Card
              className="border-gray-200 hover:shadow-md transition-all hover:border-gray-300"
              bodyStyle={{ padding: '12px 16px' }}
            >
              <div className="flex flex-col items-center text-center">
                <div className="mb-2 p-1.5 bg-gray-50 rounded-lg">
                  {card.icon}
                </div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 leading-tight">
                  {card.title}
                </div>
                <div className="text-xl font-semibold text-gray-900 mb-1">
                  {card.value}
                </div>
                <div className="text-xs text-gray-500">{card.subtitle}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Service Types Breakdown */}
      <Card 
        title={
          <span className="text-gray-900 font-semibold">Service Types Breakdown</span>
        } 
        className="border-gray-200 shadow-sm"
      >
        <Row gutter={[20, 20]}>
          {serviceMetrics && serviceMetrics.length > 0 ? (
            [
              "collection_delivery",
              "site_service",
              "warehouse",
              "fabrication",
              "packing",
            ].map((type) => {
              const count = serviceMetrics.filter(
                (m) => m.serviceType === type
              ).length;
              const activeCount = serviceMetrics.filter(
                (m) => m.serviceType === type && m.isActive
              ).length;

              return (
                <Col xs={24} sm={12} lg={8} key={type}>
                  <Card 
                    size="small" 
                    className="text-center border-gray-100 hover:border-gray-300 transition-colors"
                    bodyStyle={{ padding: '16px' }}
                  >
                    <Statistic
                      title={
                        <span className="text-gray-600 text-xs font-medium uppercase tracking-wide">
                          {type.replace("_", " ")}
                        </span>
                      }
                      value={count}
                      valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 600 }}
                      suffix={
                        <span className="text-gray-500 text-sm">
                          ({activeCount} active)
                        </span>
                      }
                    />
                  </Card>
                </Col>
              );
            })
          ) : (
            <Col span={24}>
              <Alert
                message="No service metrics found"
                description="Start by creating your first service metric."
                type="info"
                showIcon
                className="border-gray-200"
              />
            </Col>
          )}
        </Row>
      </Card>

      {/* Summary Statistics */}
      <Card 
        title={
          <span className="text-gray-900 font-semibold">Summary Statistics</span>
        } 
        className="border-gray-200 shadow-sm"
      >
        <Row gutter={[20, 20]}>
          <Col xs={24} sm={8}>
            <Statistic
              title={
                <span className="text-gray-600 text-sm">Total Service Configurations</span>
              }
              value={totalServiceMetrics}
              valueStyle={{ color: '#1f2937', fontSize: '28px', fontWeight: 600 }}
              prefix={<Calculator size={18} className="text-gray-400" />}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title={
                <span className="text-gray-600 text-sm">Staff Roles Configured</span>
              }
              value={totalStaffRoles}
              valueStyle={{ color: '#1f2937', fontSize: '28px', fontWeight: 600 }}
              prefix={<Users size={18} className="text-gray-400" />}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title={
                <span className="text-gray-600 text-sm">Packing Types Available</span>
              }
              value={totalPackingMetrics}
              valueStyle={{ color: '#1f2937', fontSize: '28px', fontWeight: 600 }}
              prefix={<Package size={18} className="text-gray-400" />}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};
